import type { AddOnOption } from "@/lib/api/types"
import type { BookingState } from "@/types/booking"
import { getAvailableAddOns } from "@/lib/booking"

export interface SingleAddOn {
  kind: "single"
  addOn: AddOnOption
}

export interface GroupedAddOn {
  kind: "grouped"
  groupKey: string
  name: string | null
  type: string | null
  priceDeltaPerPerson: number
  defaultSelected: boolean
  icon: string | null
  children: AddOnOption[]
}

export type DisplayAddOn = SingleAddOn | GroupedAddOn

function groupKey(addOn: AddOnOption): string {
  return [
    (addOn.name ?? "").trim().toLowerCase(),
    addOn.priceDeltaPerPerson,
    addOn.type ?? "",
  ].join("|")
}

export function getDisplayAddOns(state: BookingState): DisplayAddOn[] {
  const addOns = getAvailableAddOns(state)
  const groups = new Map<string, AddOnOption[]>()
  const singles: AddOnOption[] = []

  for (const addOn of addOns) {
    const hasNote = !!addOn.note?.trim()

    if (!hasNote) {
      singles.push(addOn)
      continue
    }

    const key = groupKey(addOn)
    const existing = groups.get(key)
    if (existing) {
      existing.push(addOn)
    } else {
      groups.set(key, [addOn])
    }
  }

  const result: DisplayAddOn[] = []

  for (const single of singles) {
    result.push({ kind: "single", addOn: single })
  }

  for (const [key, children] of groups) {
    if (children.length === 1) {
      result.push({ kind: "single", addOn: children[0] })
      continue
    }

    const first = children[0]
    result.push({
      kind: "grouped",
      groupKey: key,
      name: first.name,
      type: first.type,
      priceDeltaPerPerson: first.priceDeltaPerPerson,
      defaultSelected: first.defaultSelected,
      icon: first.icon,
      children,
    })
  }

  // Position first, name only to break a tie. Sorting by name alone threw away
  // the order set in the admin, which is the whole point of having one: every
  // list came out alphabetical no matter what anyone dragged.
  return result.sort((a, b) => {
    const byPosition = positionOf(a) - positionOf(b)
    if (byPosition !== 0) return byPosition

    return (nameOf(a) ?? "").localeCompare(nameOf(b) ?? "", undefined, {
      sensitivity: "base",
    })
  })
}

function nameOf(entry: DisplayAddOn): string | null {
  return entry.kind === "single" ? entry.addOn.name : entry.name
}

/** A group sits where its earliest member does — it stands in for all of them. */
function positionOf(entry: DisplayAddOn): number {
  if (entry.kind === "single") return entry.addOn.position

  return entry.children.reduce(
    (lowest, child) => Math.min(lowest, child.position),
    Number.POSITIVE_INFINITY
  )
}

export function getGroupedSelectionSummary(
  grouped: GroupedAddOn,
  selectedAddOns: Record<string, number>
): { note: string; quantity: number }[] {
  return grouped.children
    .map((child) => ({
      note: child.note ?? "",
      quantity: selectedAddOns[child.id] ?? 0,
    }))
    .filter((item) => item.quantity > 0)
}

export function isGroupedSelected(
  grouped: GroupedAddOn,
  selectedAddOns: Record<string, number>
): boolean {
  return grouped.children.some(
    (child) => (selectedAddOns[child.id] ?? 0) > 0
  )
}

export function getGroupedTotalQuantity(
  grouped: GroupedAddOn,
  selectedAddOns: Record<string, number>
): number {
  return grouped.children.reduce(
    (sum, child) => sum + (selectedAddOns[child.id] ?? 0),
    0
  )
}
